import { Paper, Skeleton, Stack } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import { ProductType, type Address, type ContactAddress } from "@repo/api";
import { AMLData } from "@repo/logic";
import { BodyTextM, cs, HeadingL, HeadingM } from "@repo/ui";
import { useFormikContext } from "formik";
import {
    useCallback,
    useEffect,
    useRef,
    type ComponentType,
    type Dispatch,
    type FC,
    type HTMLAttributes,
    type SetStateAction,
} from "react";
import { useContactAdresses, useProfile } from "../../queryHooks";
import AddressTemplate from "../AddressBox/AddressTemplate";
import Radio from "../Form/Input/Radio";
import { RadioGroup } from "../Form/Input/Radio/RadioGroup";
import Switch from "../Form/Input/Switch";
import TextInput from "../Form/Input/Text";
import Autocomplete from "../Form/Select/Autocomplete";

type ChangeAddressFormValues = {
    addressType: string;
    street: string;
    houseNumber: string;
    zip: string;
    town: string;
    country: string;
};

type CountryOption = {
    label: string;
    value: string;
};

const AutocompleteAny = Autocomplete as unknown as ComponentType<Record<string, unknown>>;

const ChangeAddress: FC<
    ContactAddress & {
        comesValidCountryCodeFromApi: boolean;
        setComesValidCountryCodeFromApi: Dispatch<SetStateAction<boolean>>;
    }
> = ({ contractNumber, title, comesValidCountryCodeFromApi, setComesValidCountryCodeFromApi }) => {
    const nullCountry = { label: "-", value: "" };
    const { values, setFieldValue, setFieldTouched } = useFormikContext<ChangeAddressFormValues>();
    const addresses = useContactAdresses();
    const profileResult = useProfile();
    const lastAppliedAddressTypeRef = useRef<string | null>(null);
    const countryOptions: CountryOption[] = AMLData.Countries.map((country) => ({
        label: country.country_name_cz,
        value: country.country_code_a3,
    }));
    const filterCountryOptions = createFilterOptions<CountryOption>({
        stringify: (option: CountryOption) => `${option.value}${option.label}`, // whisper options by values and also labels
    });

    const resetAddressTouched = useCallback((): void => {
        ["street", "houseNumber", "zip", "town", "country"].forEach((fieldName) => {
            setFieldTouched(fieldName, false, false);
        });
    }, [setFieldTouched]);

    const fillAddress = useCallback(
        (item: Address): void => {
            resetAddressTouched();
            setFieldValue("street", item.street ?? "");
            setFieldValue("houseNumber", item.houseNumber ?? "");
            setFieldValue("zip", item.zip);
            setFieldValue("town", item.town ?? "");
            setFieldValue("country", item.country ?? "");
            setComesValidCountryCodeFromApi((item.country ?? "") !== "");
        },
        [resetAddressTouched, setFieldValue, setComesValidCountryCodeFromApi],
    );

    const clearAddress = useCallback((): void => {
        resetAddressTouched();
        setFieldValue("street", "");
        setFieldValue("houseNumber", "");
        setFieldValue("zip", "");
        setFieldValue("town", "");
        setFieldValue("country", "");
    }, [resetAddressTouched, setFieldValue]);

    useEffect(() => {
        const selectedAddressType = values.addressType;

        if (!selectedAddressType || lastAppliedAddressTypeRef.current === selectedAddressType) {
            return;
        }

        if (selectedAddressType === "other") {
            clearAddress();
            lastAppliedAddressTypeRef.current = selectedAddressType;
            return;
        }

        if (selectedAddressType.startsWith("permanent")) {
            const permanentAddressIndex = Number(selectedAddressType.replace("permanent", ""));
            const selectedAddress = profileResult.data?.permanentAddresses[permanentAddressIndex];

            if (!selectedAddress) {
                return;
            }

            fillAddress(selectedAddress);
            lastAppliedAddressTypeRef.current = selectedAddressType;
            return;
        }

        if (selectedAddressType.startsWith("contact-") && addresses.isSuccess) {
            const selectedContractNumber = selectedAddressType.replace("contact-", "");
            const selectedAddress = addresses.data.contactAddresses.find(
                (item) => item.contractNumber === selectedContractNumber,
            )?.address;

            if (!selectedAddress) {
                return;
            }

            fillAddress(selectedAddress);
            lastAppliedAddressTypeRef.current = selectedAddressType;
        }
    }, [
        clearAddress,
        fillAddress,
        values.addressType,
        profileResult.data?.permanentAddresses,
        addresses.isSuccess,
        addresses.data?.contactAddresses,
    ]);

    return (
        <Stack spacing={4}>
            <Stack>
                <HeadingL>{cs.global.fillNewInfo}</HeadingL>
                <HeadingM sx={{ mb: 1 }}>{cs.global.contactAddress}</HeadingM>
                <BodyTextM>
                    {title} ({contractNumber})
                </BodyTextM>
            </Stack>
            {!addresses.isLoading &&
                addresses.data?.contactAddresses &&
                addresses.data.contactAddresses.length > 1 &&
                addresses.data.isGlobalContactAddressChangeEnabled && (
                    <Paper sx={{ p: 4 }}>
                        <Switch
                            label={cs.profile.person.changeAddressDrawer.changeAllAddresses}
                            id="changeAllAddresses"
                            name="changeAllAddresses"
                        />
                    </Paper>
                )}
            <Stack spacing={5}>
                <Paper
                    sx={{
                        py: 4,
                        px: 3,
                    }}
                >
                    <RadioGroup name="addressType">
                        {profileResult.data?.permanentAddresses.map((address, i) => {
                            const permanentIndex = String(i);
                            const permanentValue = `permanent${permanentIndex}`;
                            return (
                                <Radio
                                    name="addressType"
                                    value={permanentValue}
                                    id={permanentValue}
                                    key={`permanent-${address.type}-${address.street ?? ""}-${address.houseNumber ?? ""}-${address.zip}-${address.country ?? ""}`}
                                    label={cs.profile.person.changeAddressDrawer.sameAsPermanent}
                                    subLabel={<AddressTemplate address={address} />}
                                />
                            );
                        })}
                        {addresses.isSuccess ? (
                            addresses.data.contactAddresses.map((item) => {
                                const address = item.address;

                                if (contractNumber === item.contractNumber) {
                                    // don't let user select the same address as he is changing
                                    return null;
                                }

                                return (
                                    <Radio
                                        name="addressType"
                                        value={`contact-${item.contractNumber}`}
                                        id={`contact-${item.contractNumber}`}
                                        key={`contact-${item.contractNumber}`}
                                        label={
                                            item.type === ProductType.Cl
                                                ? `${cs.profile.person.changeAddressDrawer.sameAsLife} - ${item.title} (${item.contractNumber})`
                                                : cs.profile.person.changeAddressDrawer.sameAsPension
                                        }
                                        subLabel={<AddressTemplate address={address} />}
                                    />
                                );
                            })
                        ) : (
                            <Stack p={4}>
                                <Skeleton />
                                <Skeleton />
                                <Skeleton />
                            </Stack>
                        )}
                        <Radio
                            name="addressType"
                            value="other"
                            id="other"
                            label={cs.profile.person.changeAddressDrawer.anotherAddress}
                        />
                    </RadioGroup>
                </Paper>
                {(values.addressType === "other" || !comesValidCountryCodeFromApi) && (
                    <Paper
                        key={`manual-address-${values.addressType}`}
                        sx={{
                            p: 4,
                        }}
                    >
                        <Stack spacing={5}>
                            <TextInput name="street" label={cs.global.street} disabled={false} />
                            <TextInput name="houseNumber" label={cs.global.houseNumber} disabled={false} />
                            <TextInput name="zip" label={cs.global.zip} disabled={false} />
                            <TextInput name="town" label={cs.global.city} disabled={false} />
                            <AutocompleteAny
                                label={cs.global.country}
                                name="country"
                                options={countryOptions}
                                getOptionLabel={(option: CountryOption) => option.value}
                                filterOptions={(
                                    options: Parameters<typeof filterCountryOptions>[0],
                                    state: Parameters<typeof filterCountryOptions>[1],
                                ) => {
                                    const filteredOptions = filterCountryOptions(options, state);
                                    return [nullCountry, ...filteredOptions];
                                }}
                                renderOption={(props: HTMLAttributes<HTMLLIElement>, option: CountryOption) => (
                                    <Box component="li" {...props}>
                                        {option.label}
                                    </Box>
                                )}
                                noOptionsText={cs.global.noCountriesFound}
                            />
                        </Stack>
                    </Paper>
                )}
            </Stack>
        </Stack>
    );
};

export default ChangeAddress;
